/**
 * Site Access Utility
 * Centralized logic for site-based permissions and access control
 */

// ============ INTERNAL HELPERS ============

/**
 * Normalize ID to string (handles objects with id property)
 * @param {number|string|Object} item - ID or object with id
 * @returns {string} - Normalized string ID
 */
function normalizeId(item) {
    // Handle null/undefined
    if (item == null) {
        return '';
    }

    // Handle objects
    if (typeof item === 'object' && !Array.isArray(item)) {
        // If object has an id property, use it
        if ('id' in item && item.id != null) {
            return String(item.id);
        }
        // If no id property, return empty string
        return '';
    }

    // Handle primitives (numbers, strings)
    return String(item);
}

/**
 * Normalize array of IDs to strings (filters out invalid entries)
 * @param {Array} items - Array of IDs or objects
 * @returns {Array<string>} - Array of string IDs (empty strings filtered out)
 */
export function normalizeIds(items = []) {
    if (!Array.isArray(items)) {
        return [];
    }
    return items.map(normalizeId).filter((id) => id !== ''); // Remove empty strings from invalid items
}

/**
 * Create Set from array of IDs (normalized to strings)
 * @param {Array} items - Array of IDs or objects
 * @returns {Set<string>} - Set of string IDs
 */
function createIdSet(items = []) {
    return new Set(normalizeIds(items));
}

// ============ VIEW TYPE DETECTION ============

/**
 * Determines the current view type based on globalSiteId, userSites, isCompanyOwner, and allCompanySites
 * @param {string|number|null} globalSiteId - Currently selected global site
 * @param {number} userSites - Total number of sites the user has access to
 * @param {boolean} isCompanyOwner - Whether the user is a company owner
 * @param {number} allCompanySites - Total number of sites in the company
 * @returns {'multi-site'|'selected-site'|'single-site-user'}
 */
export function getViewType(
    globalSiteId,
    userSiteCount = 0,
    isCompanyOwner = false,
    totalCompanySitesCount = 0
) {
    // Handle single site cases first (applies to both owners and regular users)
    // if user is a company owner, use the total number of sites in the company,
    // otherwise use the number of sites the user has access to
    const siteCount = isCompanyOwner ? totalCompanySitesCount : userSiteCount;
    if (siteCount === 1) {
        return 'single-site-user';
    }

    // Handle multi-site with a specific site selected
    if (globalSiteId !== null && globalSiteId !== undefined && siteCount > 1) {
        return 'selected-site';
    }

    // Handle multi-site scenarios
    if (siteCount > 1) {
        return 'multi-site';
    }

    // Fallback for edge cases (e.g., no sites)
    return 'multi-site';
}

// ============ SITE OVERLAP CALCULATIONS ============

/**
 * Calculates the intersection of record sites and user sites
 * @param {Array} recordSites - Sites assigned to the record (array of IDs)
 * @param {Array} userSites - Sites the user can access (array of IDs)
 * @returns {Array} - Array of site IDs in both arrays
 */
export function calculateOverlap(recordSites = [], userSites = []) {
    const recordSiteSet = createIdSet(recordSites);
    return normalizeIds(userSites).filter((id) => recordSiteSet.has(id));
}

/**
 * Gets sites in record that user cannot access (hidden sites)
 * Also known as "protected sites" in form submission contexts (sites that must be preserved)
 * @param {Array} recordSites - Sites assigned to the record
 * @param {Array} userSites - Sites the user can access (array of IDs)
 * @returns {Array} - Site IDs user cannot access
 */
export function getHiddenSites(recordSites = [], userSites = []) {
    const userSiteSet = createIdSet(userSites);
    return normalizeIds(recordSites).filter((id) => !userSiteSet.has(id));
}

// ============ ACCESS CONTROL ============

/**
 * Checks if user can view and edit a record based on site access
 * @returns {Object} - { canView, canEdit, reason }
 */
export function canAccessRecord({
    recordSites,
    userSites,
    isAppAdmin,
    isCompanyOwner,
}) {
    // Admins and owners have full access
    if (isAppAdmin || isCompanyOwner) {
        return {
            canView: true,
            canEdit: true,
        };
    }

    const userSiteIds = normalizeIds(userSites);
    const recordSiteStrings = normalizeIds(recordSites);
    const overlap = calculateOverlap(recordSites, userSites);

    // No overlap - cannot view or edit
    if (overlap.length === 0) {
        return {
            canView: false,
            canEdit: false,
            reason: "You don't have access to any sites this record belongs to",
        };
    }

    // Has overlap - can view
    const allRecordSitesAccessible = recordSiteStrings.every((id) =>
        userSiteIds.includes(id)
    );

    // Can edit only if user has access to ALL record sites
    if (allRecordSitesAccessible) {
        return {
            canView: true,
            canEdit: true,
        };
    }

    // Has partial site access - can view but cannot edit
    return {
        canView: true,
        canEdit: false,
        reason: "You don't have access to all sites this record belongs to",
    };
}

// ============ DEFAULT SITES FOR NEW DOCUMENTS ============

/**
 * Gets default sites for a new document based on view type
 */
export function getDefaultSitesForNewRecord({
    viewType,
    globalSiteId,
    userSites,
}) {
    switch (viewType) {
        case 'selected-site':
            return globalSiteId ? [String(globalSiteId)] : [];
        case 'single-site-user':
            return userSites.length === 1 ? [normalizeId(userSites[0])] : [];
        case 'multi-site':
        default:
            return [];
    }
}

// ============ RESULT SITES CALCULATION ============

/**
 * Calculates final sites to submit (user selection + protected sites)
 */
export function calculateResultSites(selectedSites = [], protectedSites = []) {
    const selectedSet = createIdSet(selectedSites);
    const protectedSet = createIdSet(protectedSites);
    const resultSet = new Set([...selectedSet, ...protectedSet]);
    return Array.from(resultSet);
}

// ============ UI HELPERS ============

/**
 * Prepares site options for dropdown with access indicators
 */
export function prepareSiteOptions({
    allSites,
    userSites,
    recordSites = [],
    isFullAccess,
}) {
    const userSiteIds = createIdSet(userSites);
    const recordSiteIds = createIdSet(recordSites);

    return allSites.map((site) => {
        const siteId = String(site.id);
        const isAccessible = isFullAccess || userSiteIds.has(siteId);
        const isProtected = !isAccessible && recordSiteIds.has(siteId);

        return {
            value: siteId,
            text: site.name,
            disabled: !isAccessible,
            protected: isProtected,
            tooltip: !isAccessible
                ? "You don't have access to this site"
                : null,
        };
    });
}

// ============ SITE DISPLAY FORMATTING ============

/**
 * Formats sites for table display with "other sites" indicator
 * Used by library, audits, templates, etc.
 *
 * @param {Array} recordSites - Full array of site objects [{id, name}, ...] or single site object
 * @param {Array} userSites - User's accessible site IDs
 * @param {boolean} isFullAccess - App admin or company owner
 * @returns {string} - Formatted string like "Site A, Site B + 2 other sites"
 */
export function formatSitesDisplay(
    recordSites = [],
    userSites = [],
    isFullAccess = false
) {
    // Handle single site object (convert to array)
    const sitesArray = Array.isArray(recordSites)
        ? recordSites
        : recordSites
          ? [recordSites]
          : [];

    // Full access users see all sites
    if (isFullAccess) {
        return sitesArray.map((site) => site.name).join(', ');
    }

    const userSiteIds = createIdSet(userSites);

    // Calculate visible and hidden sites
    const visibleSites = sitesArray.filter((site) =>
        userSiteIds.has(String(site.id))
    );
    const hiddenSitesCount = sitesArray.length - visibleSites.length;

    // Build display string
    let sitesStr = visibleSites.map((site) => site.name).join(', ');
    if (hiddenSitesCount > 0) {
        const suffix =
            hiddenSitesCount === 1
                ? '+ 1 other site'
                : `+ ${hiddenSitesCount} other sites`;
        sitesStr = sitesStr ? `${sitesStr} ${suffix}` : suffix;
    }

    return sitesStr;
}
